import React, { useState, memo, FormEvent, CSSProperties, useCallback, useMemo, useEffect, useRef } from 'react';
import {
    Search, BarChart2, Network, Zap, Cpu, 
    PieChart, ArrowRight, Mail, Globe, Users, Database, LucideIcon, 
    Menu, X, Loader2, AlertCircle, Activity, Crosshair, Layers, Eye,
    FileText, Radio, Compass, Shield, Target, Aperture, Hexagon
} from 'lucide-react';

// --- Types & Interfaces ---

interface Service {
    icon: LucideIcon;
    title: string;
    description: string;
    color: string;
    glowColor: string;
    borderColor: string;
    badge?: string;
}

interface Method {
    icon: LucideIcon;
    title: string;
    description: string;
    steps: string[];
}

interface PlatformFeature {
    icon: LucideIcon;
    title: string;
    description: string;
}

interface ArticleContent {
    type: 'paragraph' | 'heading' | 'quote';
    text: string;
}

interface Article {
    id: string;
    title: string;
    excerpt: string;
    content: ArticleContent[];
    category: string;
    date: string;
    readTime: string;
    icon?: LucideIcon;
    image: string;
}

type BlogPost = Article;

interface PTERALogoProps {
    className?: string;
    width?: number | string;
    height?: number | string;
    style?: CSSProperties;
}

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    children: React.ReactNode;
    className?: string;
}

type LegalPageType = 'privacy' | 'terms';

interface LegalSection {
    title: string;
    body: string[];
}

interface LegalPageContent {
    label: string;
    title: string;
    intro: string;
    updated: string;
    sections: LegalSection[];
}

// --- Constants & Static Data ---

const LEGAL_CONTENT: Record<LegalPageType, LegalPageContent> = {
    privacy: {
        label: "Privacy Policy",
        title: "Privacy Policy",
        intro: "This Privacy Policy explains how PTERA Research collects, uses, and protects information submitted through this website and related business communications.",
        updated: "May 19, 2026",
        sections: [
            {
                title: "Information We Collect",
                body: [
                    "We collect information that you choose to provide, including your name, organization, email address, and message when you contact us or request analysis.",
                    "We may also collect limited technical information such as browser type, pages viewed, referring pages, approximate location, and analytics events used to understand website performance."
                ]
            },
            {
                title: "How We Use Information",
                body: [
                    "We use submitted information to respond to inquiries, evaluate service fit, provide requested briefings or consultations, and maintain business records.",
                    "Website analytics may be used to improve site reliability, understand interest in our services, and protect the site from misuse."
                ]
            },
            {
                title: "Sharing and Disclosure",
                body: [
                    "We do not sell personal information. We may share information with service providers that support website hosting, analytics, email, security, or professional advisory functions.",
                    "We may disclose information if required by law, regulation, court order, or a legitimate compliance request."
                ]
            },
            {
                title: "Data Retention and Security",
                body: [
                    "We retain business inquiry information for as long as needed to manage the relationship, comply with legal obligations, resolve disputes, and maintain operational records.",
                    "We use reasonable administrative and technical safeguards, but no online transmission or storage system can be guaranteed to be completely secure."
                ]
            },
            {
                title: "Your Choices",
                body: [
                    "You may contact us to request access, correction, or deletion of information you have submitted, subject to legal and operational limits.",
                    "You may opt out of non-essential communications by contacting us at info@pteraresearch.com."
                ]
            },
            {
                title: "Contact",
                body: [
                    "For privacy questions, contact PTERA Research at info@pteraresearch.com.",
                    "Office: Unit 1113, Peninsula Centre, 67 Mody Road, Tsim Sha Tsui, Kowloon, Hong Kong."
                ]
            }
        ]
    },
    terms: {
        label: "Terms of Use",
        title: "Terms of Use",
        intro: "These Terms of Use govern access to and use of the PTERA Research website, publications, inquiry forms, and related online materials.",
        updated: "May 19, 2026",
        sections: [
            {
                title: "Website Use",
                body: [
                    "This website is provided for general business information and inquiry purposes. You may not use the website for unlawful, abusive, automated, or security-disruptive activity.",
                    "Submitting an inquiry does not create a client relationship, advisory engagement, exclusivity obligation, or commitment by PTERA Research to provide services."
                ]
            },
            {
                title: "Research and Publications",
                body: [
                    "Website articles, briefings, examples, and service descriptions are informational only and should not be treated as legal, financial, investment, or professional advice.",
                    "Any engagement for research, analysis, consulting, or platform services is subject to separate written commercial terms."
                ]
            },
            {
                title: "Intellectual Property",
                body: [
                    "The website, brand assets, text, graphics, layouts, and research materials are owned by or licensed to PTERA Research unless otherwise stated.",
                    "You may not copy, republish, distribute, or commercially exploit website materials without prior written permission."
                ]
            },
            {
                title: "Third-Party Services",
                body: [
                    "The website may rely on third-party infrastructure, analytics, fonts, email clients, or hosting services. Their availability and operation are outside our direct control.",
                    "Links to third-party websites are provided for convenience only and do not imply endorsement."
                ]
            },
            {
                title: "No Warranty",
                body: [
                    "The website is provided on an as-is and as-available basis. We do not warrant that the site will be uninterrupted, error-free, or free from harmful components.",
                    "To the fullest extent permitted by law, PTERA Research disclaims liability for indirect, incidental, consequential, or special damages arising from website use."
                ]
            },
            {
                title: "Contact",
                body: [
                    "For questions about these terms, contact PTERA Research at info@pteraresearch.com.",
                    "Office: Unit 1113, Peninsula Centre, 67 Mody Road, Tsim Sha Tsui, Kowloon, Hong Kong."
                ]
            }
        ]
    }
};

const SERVICES_DATA: Service[] = [
    {
        icon: Search,
        title: "Ecosystem Mapping & Analysis",
        description: "Visualize your industry's ecosystem. Identify key actors, emerging niches, and structural shifts that inform positioning.",
        color: "from-teal-500 to-cyan-400",
        glowColor: "shadow-teal-500/20 hover:shadow-teal-500/40",
        borderColor: "hover:border-teal-500/30",
        badge: "Core"
    },
    {
        icon: Crosshair,
        title: "Predictive Market Intelligence",
        description: "Signal detection and forecasting models that anticipate directional shifts before they reach consensus.",
        color: "from-indigo-500 to-violet-400",
        glowColor: "shadow-indigo-500/20 hover:shadow-indigo-500/40",
        borderColor: "hover:border-indigo-500/30"
    },
    {
        icon: Layers,
        title: "Strategic Architecture",
        description: "Structural analysis of partnership networks and compatibility matrices for complex alliance design.",
        color: "from-emerald-500 to-teal-400",
        glowColor: "shadow-emerald-500/20 hover:shadow-emerald-500/40",
        borderColor: "hover:border-emerald-500/30",
        badge: "New"
    },
    {
        icon: Compass,
        title: "Market Positioning Analysis",
        description: "Data-backed evaluation of expansion vectors, with risk modeling and competitive field assessment.",
        color: "from-amber-500 to-orange-400",
        glowColor: "shadow-amber-500/20 hover:shadow-amber-500/40",
        borderColor: "hover:border-amber-500/30"
    },
    {
        icon: Radio,
        title: "Network Intelligence",
        description: "Monitor and analyze stakeholder dynamics, influence patterns, and emergent community structures.",
        color: "from-rose-500 to-pink-400",
        glowColor: "shadow-rose-500/20 hover:shadow-rose-500/40",
        borderColor: "hover:border-rose-500/30"
    },
    {
        icon: Activity,
        title: "Decision Systems",
        description: "Custom analytics frameworks and decision support tools calibrated to specific operating environments.",
        color: "from-sky-500 to-blue-400",
        glowColor: "shadow-sky-500/20 hover:shadow-sky-500/40",
        borderColor: "hover:border-sky-500/30"
    }
];

const METHODOLOGY_STEPS: Method[] = [
    {
        icon: Network,
        title: "Field Mapping",
        description: "Map the complete operating environment. Identify structural patterns, latent dependencies, and shifts in the competitive topology.",
        steps: ["Topology analysis", "Actor mapping", "Dependency tracing"]
    },
    {
        icon: Cpu,
        title: "Signal Processing",
        description: "Apply algorithmic analysis to detect patterns in complex datasets that standard reporting frameworks miss entirely.",
        steps: ["Pattern recognition", "Anomaly detection", "Trend decomposition"]
    },
    {
        icon: Aperture,
        title: "Strategic Synthesis",
        description: "Translate raw intelligence into coherent strategic direction. Prioritize opportunities by structural fit and timing.",
        steps: ["Opportunity ranking", "Resource modeling", "Timeline mapping"]
    },
    {
        icon: Hexagon,
        title: "Execution Support",
        description: "Advisory during implementation. Track structural changes in the field and refine positioning as conditions evolve.",
        steps: ["Change monitoring", "Adaptive refinement", "Outcome analysis"]
    }
];

const PLATFORM_FEATURES: PlatformFeature[] = [
    {
        icon: Globe,
        title: "Field Monitoring",
        description: "Real-time tracking of structural reorganization across target sectors."
    },
    {
        icon: Network,
        title: "Relationship Mapping",
        description: "Network topology of actors, influence channels, and latent connections."
    },
    {
        icon: Database,
        title: "Predictive Modeling",
        description: "Momentum estimation and directional forecasting for market variables."
    },
    {
        icon: Users,
        title: "Stakeholder Analysis",
        description: "Role classification and behavioral modeling of key field actors."
    },
    {
        icon: BarChart2,
        title: "Performance Signals",
        description: "Indicator tracking calibrated to specific strategic objectives."
    },
    {
        icon: Eye,
        title: "Intelligence Delivery",
        description: "Structured briefings with clear navigational guidance."
    }
];

const ARTICLES_DATA: Article[] = [
    {
        id: "ecosystem-orchestration",
        title: "From Rivalry to Ecosystem Orchestration",
        excerpt: "The strongest companies are no longer defined only by what they own. They are defined by the networks they make legible, useful, and hard to leave.",
        content: [
            { type: 'paragraph', text: "Competition used to be easy to draw. A company had a category, a set of rivals, a value chain, and a boundary around the assets it controlled. That picture is now too small. Most strategic outcomes are shaped by partner networks, infrastructure providers, channels, regulators, communities, and adjacent technologies that sit outside the firm but determine its room to move." },
            { type: 'heading', text: "The Boundary Has Become the Product" },
            { type: 'paragraph', text: "In a networked market, advantage comes from designing the terms of participation. The winning firm clarifies standards, reduces friction, and gives other actors a reason to build around its center of gravity. It does not need to own every node. It needs to make the network more valuable when it is present than when it is absent." },
            { type: 'paragraph', text: "That shift changes the work of strategy. The question is no longer only who can be beaten, acquired, or blocked. The sharper question is which relationships should be made easier, which dependencies should be diversified, and which incentives will cause the ecosystem to compound around a shared direction." },
            { type: 'quote', text: "Ecosystem strategy is the discipline of making external actors behave like an extended operating system." },
            { type: 'heading', text: "Orchestrators Create Gravity" },
            { type: 'paragraph', text: "Strong ecosystems have gravity. Developers want reliable APIs. Suppliers want predictable demand. Customers want compatibility. Partners want a fair path to revenue. When those needs are met in one environment, switching away becomes expensive without the orchestrator needing to rely on lock-in as the primary defense." },
            { type: 'paragraph', text: "The practical work is specific: publish clear interfaces, reduce approval cycles, expose useful data, make partner economics visible, and remove ambiguity from how value is shared. These details look operational, but together they form the strategic surface of the company." },
            { type: 'heading', text: "Map the Field Before Moving It" },
            { type: 'paragraph', text: "Orchestration fails when leaders confuse a contact list with an ecosystem map. A real map shows dependency strength, substitution risk, influence pathways, bottlenecks, and the actors whose incentives are beginning to change. It reveals where a small intervention can unlock disproportionate movement." },
            { type: 'paragraph', text: "This is where ecosystem intelligence becomes a management capability. It helps teams see when a partner is becoming a competitor, when a niche standard is becoming infrastructure, or when a fragile supplier is quietly becoming a systemic risk. The value is not more information. The value is earlier alignment." },
            { type: 'heading', text: "Resilience Beats Maximum Efficiency" },
            { type: 'paragraph', text: "The most efficient network is often brittle. The most durable network has redundancy, optionality, and enough diversity to absorb shocks. Leaders who optimize only for margin compression usually discover their hidden dependencies during a crisis. Orchestrators design for adaptation before volatility forces the issue." },
            { type: 'paragraph', text: "The strategic posture is therefore less about control and more about directed participation. The firm sets the architecture, monitors the field, and keeps incentives coherent as conditions change. In complex markets, the companies that win are the ones that make others stronger by joining them." }
        ],
        category: "Strategy",
        date: "May 15, 2026",
        readTime: "12 min read",
        icon: Network,
        image: "/images/ecosystem-orchestration-3d.jpg"
    },
    {
        id: "predictive-intelligence",
        title: "Predictive Intelligence Is a Decision System",
        excerpt: "AI forecasting is useful only when it shortens the distance between weak signals, strategic options, and executive action.",
        content: [
            { type: 'paragraph', text: "Most organizations do not suffer from a lack of dashboards. They suffer from slow interpretation. Signals arrive from customers, competitors, regulation, capital markets, hiring patterns, and supply chains, but they are reviewed in separate meetings by separate teams. By the time a pattern becomes obvious, the strategic window has narrowed." },
            { type: 'heading', text: "Forecasting Is Not Foresight" },
            { type: 'paragraph', text: "A forecast extends known data into the future. Foresight changes what the organization is prepared to do. The difference matters. A clean projection can still be strategically useless if it does not identify triggers, thresholds, alternatives, and the decisions that should be made when conditions move." },
            { type: 'paragraph', text: "Predictive intelligence should therefore be built as a decision system, not a reporting layer. It must connect external sensing to concrete choices: enter, wait, hedge, partner, divest, price differently, reposition, or allocate more capital." },
            { type: 'heading', text: "Weak Signals Need Context" },
            { type: 'paragraph', text: "AI is effective at scanning large surfaces: product launches, patent activity, executive hiring, social narratives, supplier instability, policy drafts, funding patterns, and emerging technical standards. The model can surface anomalies faster than a human team. But the signal becomes useful only when it is interpreted against industry structure." },
            { type: 'paragraph', text: "A surge in hiring may indicate expansion, but it may also indicate churn. A new patent cluster may signal genuine capability, defensive filing, or a stranded research direction. Predictive systems need human review, sector priors, and feedback loops so that novelty is not mistaken for relevance." },
            { type: 'quote', text: "The goal is not to predict one future. The goal is to make the organization harder to surprise." },
            { type: 'heading', text: "Scenarios Create Optionality" },
            { type: 'paragraph', text: "The most useful models produce scenario ranges. They show what changes if demand softens, a supplier fails, a competitor bundles aggressively, or regulation shifts the cost curve. This lets leadership test whether current strategy is robust or merely optimized for the base case." },
            { type: 'paragraph', text: "Scenario work also changes the tempo of leadership. Instead of debating from scratch during a shock, teams can agree in advance on leading indicators, decision thresholds, and pre-approved responses. Speed improves because interpretation has already been rehearsed." },
            { type: 'heading', text: "Translate Signals Into Commitments" },
            { type: 'paragraph', text: "The hardest part of predictive work is not model accuracy. It is organizational commitment. A signal that does not change budgets, roadmaps, hiring, pricing, partnership priorities, or risk posture remains an interesting observation. Useful systems define what kind of signal deserves action and which owner has the authority to move." },
            { type: 'paragraph', text: "This is where intelligence becomes operational. Teams need trigger points, confidence levels, dissent channels, and a record of decisions made from prior forecasts. Without that discipline, the organization can consume more predictions while becoming less decisive." },
            { type: 'heading', text: "The Operating Model Matters" },
            { type: 'paragraph', text: "Predictive intelligence fails when it lives in an innovation lab with no path to action. It works when ownership is clear: who monitors the field, who validates the signal, who decides, who executes, and how outcomes are fed back into the model." },
            { type: 'paragraph', text: "The strongest systems combine machine-scale sensing with executive judgment. AI widens the field of view. Humans determine consequence, timing, and acceptable risk. Together, they turn uncertainty from a vague threat into a managed operating condition." }
        ],
        category: "Technology",
        date: "May 12, 2026",
        readTime: "12 min read",
        icon: Cpu,
        image: "/images/predictive-intelligence-3d.jpg"
    }
];

const BLOG_POSTS_DATA: BlogPost[] = [
    {
        id: "invisible-web",
        title: "The Invisible Web of Dependencies",
        excerpt: "Most strategic risk sits outside the org chart. Dependency mapping exposes the suppliers, standards, and intermediaries that quietly shape resilience.",
        category: "Insights",
        date: "Apr 12, 2026",
        readTime: "12 min read",
        image: "/images/invisible-web-3d.jpg",
        content: [
            { type: 'paragraph', text: "Executives usually know their direct suppliers, largest partners, and obvious competitors. They are less likely to know the second-order actors that make the operating model possible: specialist component makers, shared logistics providers, data vendors, standards bodies, regional distributors, and infrastructure platforms." },
            { type: 'heading', text: "Hidden Dependencies Become Visible Under Stress" },
            { type: 'paragraph', text: "A minor provider can become strategic when there is no practical substitute. A policy change in one region can change input costs across several markets. A technical standard can turn an adjacent platform into a gatekeeper. These dependencies often remain invisible because they do not appear in executive reporting until they break." },
            { type: 'paragraph', text: "Dependency mapping makes these relationships explicit. It ranks nodes by substitutability, concentration, switching time, information asymmetry, and influence over downstream outcomes." },
            { type: 'quote', text: "Resilience starts when the organization can name the dependencies it used to treat as background." },
            { type: 'heading', text: "The Risk Is Usually Indirect" },
            { type: 'paragraph', text: "Direct exposure is easy to manage because it has a contract, an owner, and a familiar escalation path. Indirect exposure is harder. A company may have no formal relationship with a standards committee, cloud marketplace, logistics corridor, payment rail, or specialist talent pool, yet its margins and growth plans can still depend on how that actor behaves." },
            { type: 'paragraph', text: "The practical question is therefore not simply who supplies us. It is who can change the conditions under which our suppliers, partners, distributors, customers, and regulators operate. Once that question is asked, the relevant field becomes wider and more strategically useful." },
            { type: 'heading', text: "Maps Need Weight, Not Just Nodes" },
            { type: 'paragraph', text: "A network diagram can create false comfort if every relationship looks equally important. Strategic mapping has to assign weight. Some actors are easy to replace, some are difficult but manageable, and some create hidden choke points because they control data, certification, shelf space, integration access, or customer trust." },
            { type: 'paragraph', text: "Weighting also reveals where redundancy is worth the cost. A second supplier may look inefficient until switching time, regulatory approval, or quality validation is included. A backup technology stack may look expensive until the team models the revenue loss created by a platform outage or vendor policy change." },
            { type: 'heading', text: "The Output Is a Better Set of Choices" },
            { type: 'paragraph', text: "A useful map does not simply warn that the world is complex. It shows where to build redundancy, where to negotiate better visibility, where to diversify, and where to accept dependency because the cost of duplication is too high." },
            { type: 'paragraph', text: "It can also change commercial behavior. Procurement teams can move beyond unit price and include resilience value. Strategy teams can identify acquisition targets that remove bottlenecks rather than only add revenue. Product teams can avoid integrations that create future dependence on a fragile intermediary." },
            { type: 'heading', text: "Resilience Is a Management Rhythm" },
            { type: 'paragraph', text: "Dependency maps become stale when they are treated as one-time risk exercises. Markets reorganize. Suppliers merge. Standards shift. Platforms change incentives. Regional politics alter availability. The operating system needs a rhythm for refreshing the map and reviewing whether old assumptions still hold." },
            { type: 'paragraph', text: "The companies that handle volatility well are rarely the ones with the longest risk register. They are the ones that can see concentration early, understand where substitution is realistic, and make deliberate choices before a hidden dependency turns into an emergency." }
        ]
    },
    {
        id: "ai-boardroom",
        title: "AI in the Boardroom",
        excerpt: "AI will not replace executive judgment, but it is changing what prepared leadership looks like in fast-moving markets.",
        category: "Trends",
        date: "Mar 25, 2026",
        readTime: "12 min read",
        image: "/images/ai-boardroom-3d.jpg",
        content: [
            { type: 'paragraph', text: "The boardroom is moving from periodic review to continuous interpretation. Leaders still make the decisions, but the evidence base is changing: more external data, more scenario modeling, and more pressure to explain why a course of action is robust under uncertainty." },
            { type: 'heading', text: "The New Executive Question" },
            { type: 'paragraph', text: "The old question was: what happened last quarter? The better question is: what would need to be true for our strategy to fail? AI helps teams explore that question across more variables than a traditional planning process can handle." },
            { type: 'paragraph', text: "This changes the quality of debate. Instead of treating strategy as a polished annual narrative, leadership can test assumptions continuously and identify the indicators that deserve attention before results deteriorate." },
            { type: 'quote', text: "AI is most valuable in the boardroom when it improves the questions, not just the charts." },
            { type: 'heading', text: "Judgment Moves Upstream" },
            { type: 'paragraph', text: "As AI systems handle more sensing and synthesis, executive judgment moves earlier in the process. Leaders need to decide which domains deserve monitoring, which uncertainties matter, what evidence is acceptable, and how much ambiguity the organization is willing to tolerate before acting." },
            { type: 'paragraph', text: "That upstream judgment is strategic. A board that only reviews AI-generated outputs is late. A board that shapes the questions, stress tests assumptions, and asks how the model could be wrong changes the quality of the entire management process." },
            { type: 'heading', text: "Board Packs Become Living Systems" },
            { type: 'paragraph', text: "Traditional board materials are snapshots. They organize what management wants to discuss at a specific moment. AI-supported intelligence can turn those materials into a living system that tracks assumptions between meetings: customer concentration, regulatory pressure, competitor motion, supply risk, pricing elasticity, hiring signals, and ecosystem shifts." },
            { type: 'paragraph', text: "The value is not more slides. The value is continuity. Directors can see how a thesis changed, what evidence caused the change, where management disagreed, and which open questions are still unresolved. That record makes oversight more concrete and less dependent on narrative confidence." },
            { type: 'heading', text: "Governance Still Matters" },
            { type: 'paragraph', text: "Better models do not remove accountability. Boards need clarity on data provenance, model limits, escalation rules, and the human owner for every AI-supported recommendation. Without governance, predictive capability becomes theater." },
            { type: 'paragraph', text: "Good governance also defines where AI should not decide. Capital allocation, workforce changes, acquisitions, market exits, and safety-critical choices require accountable human ownership. The system can sharpen the evidence base, but responsibility cannot be outsourced to a model." },
            { type: 'heading', text: "The Prepared Board Is More Specific" },
            { type: 'paragraph', text: "AI raises the standard for preparedness because generic curiosity is no longer enough. Directors can ask for named indicators, scenario ranges, alternative interpretations, and the commercial consequences of being wrong. Management teams should expect sharper questions because the tools for asking them have improved." },
            { type: 'paragraph', text: "The result is a boardroom that spends less time admiring uncertainty and more time deciding how the company will behave inside it. AI does not replace strategic courage. It makes unsupported confidence easier to challenge." }
        ]
    },
    {
        id: "silos-to-synergy",
        title: "From Silos to Strategic Synergy",
        excerpt: "Partnerships work when incentives, capabilities, timing, and operating cultures line up. Compatibility can be modeled before capital is committed.",
        category: "Case Study",
        date: "Feb 10, 2026",
        readTime: "12 min read",
        image: "/images/silos-synergy-3d.jpg",
        content: [
            { type: 'paragraph', text: "Many partnerships fail before execution begins. The teams like the strategic story but ignore the operating reality: mismatched decision cycles, unclear ownership, incompatible incentives, weak data access, or different definitions of success." },
            { type: 'heading', text: "Compatibility Is Measurable" },
            { type: 'paragraph', text: "A stronger partnership process scores fit across several dimensions: capability complementarity, customer overlap, integration cost, decision speed, brand risk, technical dependency, and the durability of each party's incentive to keep investing." },
            { type: 'paragraph', text: "The output is not a single magic score. It is a decision map that shows where the partnership is structurally strong, where it needs governance, and where the deal depends on assumptions that should be tested before launch." },
            { type: 'quote', text: "Synergy is not created by proximity. It is created by aligned incentives under real operating constraints." },
            { type: 'heading', text: "Silos Are Often Rational" },
            { type: 'paragraph', text: "Organizations do not create silos only because people refuse to collaborate. They create them because teams are measured differently, protect different risks, operate on different timelines, and serve different internal customers. A partnership that ignores those incentives is asking people to behave against the system around them." },
            { type: 'paragraph', text: "Strategic synergy begins by making those constraints visible. Sales may want speed, product may need quality control, legal may need risk containment, and finance may need predictable unit economics. The work is not to pretend those tensions disappear. It is to design an alliance where they can be managed without stopping progress." },
            { type: 'heading', text: "Shared Metrics Change Behavior" },
            { type: 'paragraph', text: "A collaboration cannot run on separate scoreboards forever. If one partner measures adoption, another measures margin, and a third measures integration stability, the alliance will drift unless the shared metrics are explicit. The metrics do not have to be identical, but they must be compatible enough to keep investment pointed in the same direction." },
            { type: 'paragraph', text: "The best measures combine activity, quality, and outcome. They track whether teams are moving, whether the work is creating dependable operating value, and whether the partnership is improving the strategic position of each participant. That combination prevents theater, where meetings increase but capability does not." },
            { type: 'heading', text: "Design the Alliance Before Announcing It" },
            { type: 'paragraph', text: "The best alliances define escalation paths, shared metrics, data rights, and exit conditions early. That discipline makes collaboration faster because the hard questions have already been answered." },
            { type: 'paragraph', text: "This design work should include negative scenarios. What happens if demand is lower than expected, integration takes twice as long, a regulator asks for documentation, or one side changes leadership? A strong alliance is not one that assumes permanent harmony. It is one that can keep moving when conditions become inconvenient." },
            { type: 'heading', text: "Synergy Has to Compound" },
            { type: 'paragraph', text: "The first phase of a partnership often proves whether two organizations can execute together. The second phase proves whether the relationship can compound. That means shared learning, reusable interfaces, better data exchange, faster governance, and a clearer view of where the next opportunity should come from." },
            { type: 'paragraph', text: "When synergy compounds, the alliance becomes more than a transaction. It becomes a strategic capability: a repeatable way to combine assets, reduce uncertainty, and enter adjacent markets with less friction than either organization could manage alone." }
        ]
    }
];

const ALL_ARTICLES_DATA: Article[] = [...ARTICLES_DATA, ...BLOG_POSTS_DATA];

const getArticleSlug = (article: Article): string =>
    article.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

const NAV_ITEMS = ['Services', 'Methodology', 'Platform', 'Insights', 'Contact'] as const;

// --- Hooks ---

const useScrollReveal = (threshold = 0.1) => {
    const ref = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.unobserve(el);
                }
            },
            { threshold }
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, [threshold]);

    return { ref, isVisible };
};

// Static animation delay styles (prevents inline style object creation)
const ANIMATION_DELAYS = {
    hero1: { animationDelay: '0.1s' } as const,
    hero2: { animationDelay: '0.3s' } as const,
    hero3: { animationDelay: '0.5s' } as const,
    blob2: { animationDelay: '2s' } as const,
    blob3: { animationDelay: '4s' } as const,
};

// --- Memoized Components ---

const PTERALogo = memo(({ className = "", width = 40, height = 40, style = {} }: PTERALogoProps) => (
    <svg
        width={width}
        height={height}
        viewBox="0 0 200 200"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        style={style}
    >
        <defs>
            <linearGradient id="torusGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#0d9488" />
                <stop offset="50%" stopColor="#6366f1" />
                <stop offset="100%" stopColor="#8b5cf6" />
            </linearGradient>
        </defs>
        <g transform="translate(100 100)">
            {[...Array(12)].map((_, i) => (
                <ellipse
                    key={i}
                    cx="0"
                    cy="0"
                    rx="80"
                    ry="25"
                    transform={`rotate(${i * 15})`}
                    stroke="url(#torusGradient)"
                    strokeWidth="1.5"
                    strokeOpacity="0.5"
                    fill="none"
                />
            ))}
            <circle cx="0" cy="0" r="12" fill="url(#torusGradient)" opacity="0.6" />
        </g>
    </svg>
));
PTERALogo.displayName = 'PTERALogo';

const Button = memo(({ children, className = '', ...props }: ButtonProps) => (
    <button
        className={`px-5 py-2.5 sm:px-6 sm:py-3 rounded-lg font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] ${className}`}
        {...props}
    >
        {children}
    </button>
));
Button.displayName = 'Button';

const ServiceCard = memo(({ service }: { service: Service }) => (
    <div className={`group glass-card glass-card-teal rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-1 ${service.borderColor}`}>
        <div className="px-6 py-8 flex flex-col h-full relative">
            <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-[0.03] transition-opacity duration-500`} />
            <div className="mb-6 flex items-center justify-between relative z-10">
                <div className={`bg-gradient-to-br ${service.color} rounded-xl p-3.5 flex items-center justify-center shadow-lg ${service.glowColor} group-hover:scale-110 transition-all duration-500`}>
                    <service.icon className="w-6 h-6 text-white" />
                </div>
                {service.badge && (
                    <span className="text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider bg-slate-700/50 text-slate-300 border border-slate-600/30">
                        {service.badge}
                    </span>
                )}
            </div>
            <h3 className="text-lg font-heading font-bold mb-3 text-white group-hover:text-slate-200 transition-colors duration-300">
                {service.title}
            </h3>
            <p className="text-slate-400 text-sm flex-grow leading-relaxed font-light group-hover:text-slate-300 transition-colors">
                {service.description}
            </p>
        </div>
    </div>
));
ServiceCard.displayName = 'ServiceCard';

const MethodologyCard = memo(({ method, stepNumber }: { method: Method; stepNumber: number }) => (
    <div className="relative glass-card glass-card-indigo rounded-2xl p-8 transition-all duration-500 hover:bg-slate-800/60 group hover:border-indigo-500/20">
        <div className="absolute -top-5 -left-5 w-14 h-14 rounded-2xl bg-gradient-to-br from-slate-700 to-slate-600 flex items-center justify-center shadow-xl shadow-slate-900/40 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 z-20 border border-slate-500/20">
            <method.icon className="w-7 h-7 text-slate-300" />
        </div>
        <div className="absolute top-4 right-6 text-6xl font-heading font-bold text-slate-700/30 group-hover:text-indigo-500/10 transition-colors duration-500 select-none">
            {String(stepNumber).padStart(2, '0')}
        </div>
        <div className="pl-6 pt-6 relative z-10">
            <h3 className="text-xl font-heading font-bold mb-3 text-white group-hover:text-slate-200 transition-colors">{method.title}</h3>
            <p className="text-slate-400 text-sm mb-6 leading-relaxed font-light">{method.description}</p>
            <ul className="space-y-2">
                {method.steps.map((step, stepIndex) => (
                    <li key={stepIndex} className="flex items-center text-slate-400 text-sm group-hover:text-slate-300 transition-colors">
                        <div className="w-1.5 h-1.5 bg-slate-500 rounded-full mr-3 group-hover:bg-indigo-400 group-hover:scale-150 transition-all duration-300"></div>
                        {step}
                    </li>
                ))}
            </ul>
        </div>
    </div>
));
MethodologyCard.displayName = 'MethodologyCard';

const PlatformFeatureCard = memo(({ feature }: { feature: PlatformFeature }) => (
    <div className="glass-card glass-card-emerald rounded-xl p-5 hover:bg-slate-800/60 transition-all duration-300 group">
        <div className="flex items-start">
            <div className="bg-emerald-500/10 rounded-lg p-2.5 mr-4 group-hover:bg-emerald-500/15 transition-colors border border-emerald-500/10">
                <feature.icon className="w-5 h-5 text-emerald-400/80 group-hover:text-emerald-300" />
            </div>
            <div>
                <h3 className="text-sm font-bold text-slate-200 mb-1.5 group-hover:text-white transition-colors">{feature.title}</h3>
                <p className="text-slate-500 text-xs leading-relaxed group-hover:text-slate-400">{feature.description}</p>
            </div>
        </div>
    </div>
));
PlatformFeatureCard.displayName = 'PlatformFeatureCard';

const ArticleCard = memo(({ article, onClick }: { article: Article; onClick: (article: Article) => void }) => (
    <div 
        className="group relative flex h-full cursor-pointer flex-col overflow-hidden rounded-2xl border border-slate-800/60 bg-slate-950/70 transition-all duration-500 hover:-translate-y-1 hover:border-amber-400/30 hover:shadow-2xl hover:shadow-amber-950/20"
        onClick={() => onClick(article)}
    >
        <div className="relative h-48 sm:h-56 lg:h-60 overflow-hidden bg-slate-900">
            <img 
                src={article.image} 
                alt={article.title} 
                className="h-full w-full object-cover opacity-90 saturate-125 transition-all duration-700 group-hover:scale-105 group-hover:opacity-100"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent"></div>
            <div className="absolute left-5 top-5 flex items-center gap-2">
                <span className="rounded-full border border-white/15 bg-slate-950/55 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white/85 backdrop-blur-md">
                    {article.category}
                </span>
            </div>
            <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between text-[10px] font-medium uppercase tracking-[0.22em] text-white/65">
                <span>{article.date}</span>
                <span>{article.readTime}</span>
            </div>
        </div>
        <div className="flex flex-grow flex-col p-7">
            <div className="mb-5 flex items-center justify-between">
                <div className="h-px flex-1 bg-gradient-to-r from-amber-300/40 to-transparent"></div>
                <div className="ml-4 rounded-lg border border-amber-400/10 bg-amber-400/10 p-2">
                    {article.icon && <article.icon className="h-4 w-4 text-amber-300/90" />}
                </div>
            </div>
            <h3 className="mb-4 line-clamp-2 text-2xl font-heading font-bold leading-tight tracking-tight text-white transition-colors group-hover:text-amber-50">
                {article.title}
            </h3>
            <p className="mb-8 line-clamp-3 text-sm font-light leading-relaxed text-slate-400 transition-colors group-hover:text-slate-300">
                {article.excerpt}
            </p>
            <div className="mt-auto flex items-center justify-between border-t border-slate-800/70 pt-5">
                <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-600">Briefing</span>
                <button className="flex items-center text-[10px] font-bold uppercase tracking-widest text-slate-400 transition-colors hover:text-amber-300">
                    Open <ArrowRight className="ml-2 h-3 w-3 transition-transform group-hover:translate-x-1" />
                </button>
            </div>
        </div>
    </div>
));
ArticleCard.displayName = 'ArticleCard';

const ArticleView = memo(({ article, onBack }: { article: Article; onBack: () => void }) => {
    const [scrollProgress, setScrollProgress] = useState(0);

    useEffect(() => {
        const handleScroll = () => {
            const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
            const progress = (window.scrollY / totalHeight) * 100;
            setScrollProgress(progress);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <div className="min-h-screen bg-slate-950 text-slate-200 font-sans selection:bg-teal-500/30">
            <div 
                className="fixed top-0 left-0 h-[2px] bg-gradient-to-r from-teal-500 via-indigo-500 to-violet-500 z-[100] transition-all duration-100"
                style={{ width: `${scrollProgress}%` }}
            />

            <div className="fixed top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
                <div className="absolute top-[-10%] left-[-10%] w-[60%] h-[60%] rounded-full bg-teal-900/10 blur-[120px]" />
                <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-indigo-900/10 blur-[120px]" />
            </div>

            <nav className="relative z-50 bg-slate-950/60 backdrop-blur-xl border-b border-slate-800/40 sticky top-0">
                <div className="container mx-auto px-4 sm:px-6 py-4 flex items-center justify-between max-w-5xl">
                    <button 
                        onClick={onBack}
                        className="flex items-center text-slate-400 hover:text-white transition-colors group"
                    >
                        <ArrowRight className="w-4 h-4 mr-2 rotate-180 group-hover:-translate-x-1 transition-transform" />
                        <span className="text-xs font-bold uppercase tracking-[0.2em]">Back</span>
                    </button>
                    <div className="flex items-center group cursor-pointer" onClick={onBack}>
                        <PTERALogo width={24} height={24} className="mr-2 group-hover:rotate-12 transition-transform duration-500" />
                        <span className="text-sm font-bold text-white tracking-tight">PTERA</span>
                    </div>
                </div>
            </nav>

            <main className="relative z-10">
                <header className="relative min-h-[78vh] overflow-hidden">
                    <img 
                        src={article.image} 
                        alt={article.title} 
                        className="absolute inset-0 h-full w-full object-cover opacity-65 saturate-125"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-slate-950/15"></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-slate-950/30"></div>
                    <div className="relative flex min-h-[78vh] items-end px-4 pb-14 pt-32 sm:px-6 lg:pb-20">
                        <ScrollReveal>
                            <div className="container mx-auto max-w-6xl">
                                <div>
                                    <div className="mb-6 inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/[0.06] px-4 py-1.5 backdrop-blur-md">
                                        <span className="h-1.5 w-1.5 rounded-full bg-amber-300"></span>
                                        <span className="category-label text-slate-300">{article.category}</span>
                                    </div>
                                    <h1 className="max-w-4xl text-4xl font-heading font-bold leading-[0.95] tracking-tight text-white drop-shadow-2xl sm:text-6xl lg:text-7xl">
                                        {article.title}
                                    </h1>
                                    <p className="mt-8 max-w-2xl text-lg font-light leading-relaxed text-slate-300 sm:text-xl">
                                        {article.excerpt}
                                    </p>
                                </div>
                            </div>
                        </ScrollReveal>
                    </div>
                </header>

                <article className="container mx-auto max-w-3xl px-4 py-20 sm:px-6 sm:py-28">
                    <div className="space-y-10">
                        {article.content.map((item, idx) => {
                            if (item.type === 'heading') {
                                return (
                                    <ScrollReveal key={idx} delay={100}>
                                        <h2 className="border-t border-slate-800/70 pt-10 text-2xl font-heading font-bold tracking-tight text-white sm:text-3xl">
                                            {item.text}
                                        </h2>
                                    </ScrollReveal>
                                );
                            }
                            if (item.type === 'quote') {
                                return (
                                    <ScrollReveal key={idx} delay={200}>
                                        <div className="relative my-12 overflow-hidden rounded-2xl border border-amber-300/15 bg-gradient-to-br from-amber-300/10 via-slate-900/60 to-teal-400/5 px-7 py-9 sm:px-10">
                                            <div className="absolute right-6 top-4 text-7xl font-serif leading-none text-amber-200/10">"</div>
                                            <p className="relative text-xl font-heading font-light italic leading-relaxed text-slate-200 sm:text-2xl">
                                                {item.text}
                                            </p>
                                        </div>
                                    </ScrollReveal>
                                );
                            }
                            return (
                                <ScrollReveal key={idx}>
                                    <p className={`${idx === 0 ? 'text-xl sm:text-2xl text-slate-200 leading-relaxed font-light' : 'text-base sm:text-lg text-slate-400 leading-relaxed font-light'}`}>
                                        {item.text}
                                    </p>
                                </ScrollReveal>
                            );
                        })}
                    </div>

                    <footer className="mt-32 pt-12 border-t border-slate-800/60 flex flex-col sm:flex-row justify-between items-center gap-8">
                        <button 
                            onClick={onBack}
                            className="flex items-center text-slate-400 hover:text-white transition-colors group"
                        >
                            <ArrowRight className="w-4 h-4 mr-2 rotate-180 group-hover:-translate-x-1 transition-transform" />
                            <span className="text-xs font-bold uppercase tracking-[0.2em]">Back to Insights</span>
                        </button>
                        <div className="flex items-center space-x-4 opacity-40">
                            <PTERALogo width={20} height={20} />
                            <span className="text-[10px] text-slate-500 uppercase tracking-[0.3em]">PTERA Research</span>
                        </div>
                    </footer>
                </article>
            </main>
        </div>
    );
});
ArticleView.displayName = 'ArticleView';

const LegalPage = memo(({
    page,
    onNavigateHome,
    onNavigateLegal
}: {
    page: LegalPageType;
    onNavigateHome: () => void;
    onNavigateLegal: (page: LegalPageType) => void;
}) => {
    const content = LEGAL_CONTENT[page];
    const alternatePage: LegalPageType = page === 'privacy' ? 'terms' : 'privacy';

    return (
        <div className="min-h-screen bg-slate-950 text-slate-200 font-sans selection:bg-teal-500/30">
            <div className="fixed top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
                <div className="absolute top-[-10%] left-[-10%] w-[60%] h-[60%] rounded-full bg-teal-900/10 blur-[120px]" />
                <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-indigo-900/10 blur-[120px]" />
            </div>

            <nav className="relative z-50 bg-slate-950/60 backdrop-blur-xl border-b border-slate-800/40 sticky top-0">
                <div className="container mx-auto px-4 sm:px-6 py-4 flex items-center justify-between max-w-5xl">
                    <button
                        onClick={onNavigateHome}
                        className="flex items-center text-slate-400 hover:text-white transition-colors group"
                    >
                        <ArrowRight className="w-4 h-4 mr-2 rotate-180 group-hover:-translate-x-1 transition-transform" />
                        <span className="text-xs font-bold uppercase tracking-[0.2em]">Home</span>
                    </button>
                    <button className="flex items-center group" onClick={onNavigateHome}>
                        <PTERALogo width={24} height={24} className="mr-2 group-hover:rotate-12 transition-transform duration-500" />
                        <span className="text-sm font-bold text-white tracking-tight">PTERA Research</span>
                    </button>
                </div>
            </nav>

            <main className="relative z-10">
                <section className="container mx-auto px-4 sm:px-6 max-w-4xl pt-20 sm:pt-28 pb-12">
                    <div className="inline-flex items-center mb-8 px-4 py-1.5 rounded-full bg-slate-800/50 border border-slate-700/40">
                        <Shield className="w-3.5 h-3.5 text-teal-400/80 mr-2" />
                        <span className="category-label text-teal-400/80">Legal</span>
                    </div>
                    <h1 className="text-4xl sm:text-6xl font-heading font-bold text-white tracking-tight mb-6">
                        {content.title}
                    </h1>
                    <p className="text-base sm:text-lg text-slate-400 max-w-3xl leading-relaxed font-light">
                        {content.intro}
                    </p>
                    <p className="mt-6 text-[10px] uppercase tracking-[0.25em] text-slate-600 font-mono">
                        Last updated {content.updated}
                    </p>
                </section>

                <section className="container mx-auto px-4 sm:px-6 max-w-4xl pb-20 sm:pb-28">
                    <div className="space-y-5">
                        {content.sections.map((section) => (
                            <div key={section.title} className="glass-card rounded-xl border border-slate-800/40 p-6 sm:p-8">
                                <h2 className="text-lg sm:text-xl font-heading font-bold text-white mb-4">
                                    {section.title}
                                </h2>
                                <div className="space-y-3">
                                    {section.body.map((paragraph) => (
                                        <p key={paragraph} className="text-sm sm:text-base text-slate-400 leading-relaxed font-light">
                                            {paragraph}
                                        </p>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between border-t border-slate-800/40 pt-8">
                        <button
                            onClick={() => onNavigateLegal(alternatePage)}
                            className="text-left text-slate-400 hover:text-white transition-colors text-sm"
                        >
                            View {LEGAL_CONTENT[alternatePage].label}
                        </button>
                        <a href="mailto:info@pteraresearch.com" className="flex items-center text-slate-400 hover:text-teal-400 transition-colors text-sm">
                            <Mail className="w-4 h-4 mr-2" />
                            info@pteraresearch.com
                        </a>
                    </div>
                </section>
            </main>
        </div>
    );
});
LegalPage.displayName = 'LegalPage';

const BlogItem = memo(({ post, onClick }: { post: BlogPost; onClick: (post: BlogPost) => void }) => (
    <div 
        className="group cursor-pointer rounded-xl border border-transparent p-3 transition-all duration-300 hover:border-slate-700/50 hover:bg-white/[0.03]"
        onClick={() => onClick(post)}
    >
        <div className="flex items-start gap-4">
            <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-xl border border-slate-700/50 bg-slate-900">
                <img src={post.image} alt={post.title} className="h-full w-full object-cover opacity-75 saturate-125 transition-all duration-500 group-hover:scale-105 group-hover:opacity-100" />
            </div>
            <div className="min-w-0 flex-grow">
                <div className="mb-2 flex items-center justify-between gap-3">
                    <span className="text-[9px] font-bold text-slate-500 uppercase tracking-[0.2em]">{post.category}</span>
                    <span className="shrink-0 text-[9px] font-medium uppercase tracking-widest text-slate-600">{post.readTime}</span>
                </div>
                <h4 className="mb-1 text-base font-bold leading-tight text-slate-200 transition-colors group-hover:text-white">{post.title}</h4>
                <p className="line-clamp-2 text-xs font-light leading-relaxed text-slate-500 transition-colors group-hover:text-slate-400">{post.excerpt}</p>
            </div>
            <div className="mt-1 flex-shrink-0 translate-x-3 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100">
                <ArrowRight className="h-4 w-4 text-slate-500" />
            </div>
        </div>
    </div>
));
BlogItem.displayName = 'BlogItem';

const PlatformPreview = memo(() => (
    <div className="relative group max-w-4xl mx-auto">
        <div className="absolute -inset-0.5 bg-gradient-to-r from-teal-500/20 via-indigo-500/20 to-violet-500/20 rounded-xl blur-lg opacity-40 group-hover:opacity-60 transition duration-1000"></div>
        <div className="relative rounded-xl overflow-hidden bg-slate-950 border border-slate-800/60 shadow-2xl">
            <div className="bg-slate-900/80 border-b border-slate-800/60 p-3 flex items-center justify-between">
                <div className="flex space-x-2">
                    <div className="w-3 h-3 rounded-full bg-red-500/40"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500/40"></div>
                    <div className="w-3 h-3 rounded-full bg-emerald-500/40"></div>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400/60 animate-pulse"></div>
                    <div className="bg-slate-900/50 rounded text-[10px] text-slate-600 px-3 py-1 font-mono">ptera-platform.app</div>
                </div>
            </div>
            
            <div className="p-4 sm:p-6 bg-slate-950/90 min-h-[300px] grid grid-cols-12 gap-4">
                <div className="hidden sm:block col-span-2 space-y-3 pt-2">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="h-8 w-full bg-slate-800/30 rounded-md border border-slate-800/40" style={{ opacity: 0.5 + (i * 0.1) }}></div>
                    ))}
                </div>
                
                <div className="col-span-12 sm:col-span-10 grid grid-cols-12 gap-4">
                    <div className="col-span-12 grid grid-cols-3 gap-4">
                        {[
                            { swatch: 'bg-teal-500/10 border-teal-500/20', h: 65 },
                            { swatch: 'bg-indigo-500/10 border-indigo-500/20', h: 45 },
                            { swatch: 'bg-emerald-500/10 border-emerald-500/20', h: 80 }
                        ].map((item, i) => (
                            <div key={i} className="h-24 bg-slate-800/20 border border-slate-800/40 rounded-lg p-3 flex flex-col justify-between hover:bg-slate-800/30 transition-colors">
                                <div className={`w-6 h-6 rounded border ${item.swatch}`}></div>
                                <div className="w-16 h-2 bg-slate-700/40 rounded"></div>
                                <div className="w-10 h-4 bg-slate-600/30 rounded"></div>
                            </div>
                        ))}
                    </div>
                    
                    <div className="col-span-12 sm:col-span-8 h-48 bg-slate-800/20 border border-slate-800/40 rounded-lg p-4 relative overflow-hidden">
                        <div className="absolute top-3 left-4 right-4 flex justify-between text-[10px] text-slate-600 font-mono">
                            <span>MOMENTUM</span>
                            <span className="flex items-center gap-1"><span className="w-1 h-1 rounded-full bg-emerald-400/60"></span>LIVE</span>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 h-[75%] flex items-end justify-around px-4 pb-4">
                            {[40, 70, 45, 90, 65, 85, 60].map((h, i) => (
                                <div key={i} className="w-4 rounded-t transition-all duration-700 ease-out group-hover:opacity-100" 
                                    style={{ 
                                        height: `${h}%`, 
                                        transitionDelay: `${i * 80}ms`,
                                        background: `linear-gradient(to top, rgba(13, 148, 136, ${0.3 + (i % 3) * 0.15}), rgba(99, 102, 241, ${0.2 + (i % 2) * 0.1}))`
                                    }}>
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    <div className="col-span-12 sm:col-span-4 h-48 bg-slate-800/20 border border-slate-800/40 rounded-lg p-3 space-y-2">
                        <div className="flex items-center justify-between mb-3">
                            <div className="w-20 h-2 bg-slate-700/30 rounded"></div>
                            <div className="w-2 h-2 rounded-full bg-amber-400/40 animate-pulse"></div>
                        </div>
                        {[1, 2, 3, 4].map(i => (
                            <div key={i} className="flex items-center space-x-2 py-1">
                                <div className="w-6 h-6 rounded-full bg-slate-700/30 border border-slate-700/40"></div>
                                <div className="flex-1 h-2 bg-slate-700/20 rounded"></div>
                                <div className="w-8 h-2 bg-slate-600/20 rounded"></div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    </div>
));
PlatformPreview.displayName = 'PlatformPreview';

const ScrollReveal = memo(({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) => {
    const { ref, isVisible } = useScrollReveal();
    return (
        <div
            ref={ref}
            className={`transition-all duration-700 ${className} ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: `${delay}ms` }}
        >
            {children}
        </div>
    );
});
ScrollReveal.displayName = 'ScrollReveal';

// --- Main Component ---

const getLegalPageFromPath = (): LegalPageType | null => {
    if (typeof window === 'undefined') return null;

    const path = window.location.pathname.replace(/\/+$/, '') || '/';
    if (path === '/privacy') return 'privacy';
    if (path === '/terms') return 'terms';
    return null;
};

const getArticleFromPath = (): Article | null => {
    if (typeof window === 'undefined') return null;

    const match = window.location.pathname.replace(/\/+$/, '').match(/^\/article\/([^/]+)$/);
    if (!match) return null;

    const pathSlug = decodeURIComponent(match[1]).toLowerCase();
    return ALL_ARTICLES_DATA.find((article) => getArticleSlug(article) === pathSlug || article.id === pathSlug) ?? null;
};

const PTERAResearch = () => {
    const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
    const [formSubmitting, setFormSubmitting] = useState<boolean>(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [mobileNavOpen, setMobileNavOpen] = useState<boolean>(false);
    const [selectedArticle, setSelectedArticle] = useState<Article | null>(() => getArticleFromPath());
    const [legalPage, setLegalPage] = useState<LegalPageType | null>(() => getLegalPageFromPath());
    const [isScrolled, setIsScrolled] = useState<boolean>(false);
    const [activeSection, setActiveSection] = useState<string>('');

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 12);
            const sections = NAV_ITEMS.map((item) => item.toLowerCase());
            const offset = 120;
            let current = '';
            for (const id of sections) {
                const el = document.getElementById(id);
                if (el && el.getBoundingClientRect().top <= offset) current = id;
            }
            setActiveSection(current);
        };
        handleScroll();
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const handleRouteChange = () => {
            setLegalPage(getLegalPageFromPath());
            setSelectedArticle(getArticleFromPath());
            setMobileNavOpen(false);
        };

        window.addEventListener('popstate', handleRouteChange);
        return () => window.removeEventListener('popstate', handleRouteChange);
    }, []);

    const navigateHome = useCallback(() => {
        setSelectedArticle(null);
        setLegalPage(null);
        setMobileNavOpen(false);
        if (window.location.pathname !== '/') {
            window.history.pushState({}, '', '/');
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const navigateToLegalPage = useCallback((page: LegalPageType) => {
        setSelectedArticle(null);
        setLegalPage(page);
        setMobileNavOpen(false);
        const targetPath = `/${page}`;
        if (window.location.pathname !== targetPath) {
            window.history.pushState({}, '', targetPath);
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const scrollToSection = useCallback((sectionId: string) => {
        if (selectedArticle) setSelectedArticle(null);
        if (window.location.pathname !== '/') {
            window.history.pushState({}, '', '/');
            setLegalPage(null);
        }
        setTimeout(() => {
            document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
        setMobileNavOpen(false);
    }, [selectedArticle]);

    const handleArticleClick = useCallback((article: Article) => {
        setSelectedArticle(article);
        setLegalPage(null);
        const targetPath = `/article/${getArticleSlug(article)}`;
        if (window.location.pathname !== targetPath) {
            window.history.pushState({}, '', targetPath);
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const handleBackToHome = useCallback(() => {
        setSelectedArticle(null);
        setLegalPage(null);
        if (window.location.pathname !== '/') {
            window.history.pushState({}, '', '/');
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const handleFormSubmit = useCallback((e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setFormSubmitting(true);
        setFormError(null);

        const formData = new FormData(e.currentTarget);
        const data = Object.fromEntries(formData) as { [key: string]: string };

        if (!data.firstName || !data.lastName || !data.email || !data.message) {
            setFormError('Please fill in all required fields.');
            setFormSubmitting(false);
            return;
        }

        const subject = "Consultation Request from " + data.firstName + " " + data.lastName;
        const body = `
Name: ${data.firstName} ${data.lastName}
Company: ${data.company || 'N/A'}
Email: ${data.email}
Message: ${data.message}
        `.trim();

        setTimeout(() => {
            setFormSubmitting(false);
            setFormSubmitted(true);
            window.location.href = `mailto:info@pteraresearch.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
            e.currentTarget.reset();

            setTimeout(() => {
                setFormSubmitted(false);
            }, 6000);
        }, 800);
    }, []);

    const featurePills = useMemo(() => [
        { icon: Zap, label: "Signal Tracking" },
        { icon: Cpu, label: "Pattern Recognition" },
        { icon: PieChart, label: "Decision Views" }
    ], []);

    const contactBenefits = useMemo(() => [
        { text: "Structured analysis of complex operating environments" },
        { text: "Predictive models calibrated to your sector" },
        { text: "Network topology and stakeholder mapping" },
        { text: "Intelligence briefings with clear navigational guidance" }
    ], []);

    if (legalPage) {
        return <LegalPage page={legalPage} onNavigateHome={navigateHome} onNavigateLegal={navigateToLegalPage} />;
    }

    if (selectedArticle) {
        return <ArticleView article={selectedArticle} onBack={handleBackToHome} />;
    }

    return (
        <div className="min-h-screen bg-slate-950 text-slate-200 font-sans relative">
            <div className="relative min-h-screen bg-slate-950 font-body dot-grid">
                <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
                    <div className="absolute top-[-10%] left-[-10%] w-[60%] h-[60%] rounded-full bg-teal-900/15 blur-[120px]" />
                    <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-indigo-900/10 blur-[120px]" />
                    <div className="absolute top-[40%] left-[40%] w-[40%] h-[40%] rounded-full bg-violet-900/8 blur-[100px]" />
                </div>

                <header className={`sticky top-0 z-50 backdrop-blur-2xl transition-all duration-300 ${isScrolled ? 'bg-slate-950/85 border-b border-slate-800/60 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.6)]' : 'bg-slate-950/40 border-b border-slate-800/20'}`}>
                    <div className={`container mx-auto px-4 sm:px-6 transition-all duration-300 ${isScrolled ? 'py-3' : 'py-5'}`}>
                        <div className="relative flex items-center justify-between max-w-7xl mx-auto">
                            <button
                                type="button"
                                className="flex w-fit items-center group cursor-pointer rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
                                onClick={navigateHome}
                            >
                                <PTERALogo className="mr-3 group-hover:rotate-12 transition-transform duration-500" width={32} height={32} />
                                <div>
                                    <h1 className="text-lg sm:text-xl font-heading font-bold text-white tracking-tight group-hover:text-slate-200 transition-colors">PTERA Research</h1>
                                </div>
                            </button>
                            <nav className="hidden md:flex flex-1 mx-10 lg:mx-16" aria-label="Primary">
                                <ul className="flex items-center justify-between w-full">
                                    {NAV_ITEMS.map((item) => {
                                        const id = item.toLowerCase();
                                        const isActive = activeSection === id;
                                        return (
                                            <li key={item}>
                                                <button
                                                    type="button"
                                                    aria-current={isActive ? 'page' : undefined}
                                                    className={`relative py-2 px-1 text-[13px] font-medium tracking-wide transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-400/60 focus-visible:ring-offset-4 focus-visible:ring-offset-slate-950 rounded-sm group ${isActive ? 'text-white' : 'text-slate-400 hover:text-white'}`}
                                                    onClick={() => scrollToSection(id)}
                                                >
                                                    {item}
                                                    <span className={`pointer-events-none absolute left-1 right-1 -bottom-0.5 h-px bg-gradient-to-r from-teal-400/80 via-indigo-400/80 to-violet-400/80 origin-left transition-transform duration-300 ${isActive ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`} />
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </nav>
                            <div className="flex items-center justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => scrollToSection('contact')}
                                    className="hidden md:inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-900/50 px-4 py-1.5 text-[12px] font-medium text-slate-200 backdrop-blur transition-all duration-200 hover:border-teal-400/40 hover:bg-slate-900/80 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
                                >
                                    <span className="relative flex h-1.5 w-1.5">
                                        <span className="absolute inline-flex h-full w-full rounded-full bg-teal-400/60 opacity-75 animate-ping" />
                                        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-teal-400" />
                                    </span>
                                    Request Briefing
                                </button>
                                <button
                                    type="button"
                                    className="md:hidden p-2 text-slate-400 hover:text-white transition-colors rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
                                    onClick={() => setMobileNavOpen(!mobileNavOpen)}
                                    aria-label="Toggle menu"
                                    aria-expanded={mobileNavOpen}
                                >
                                    {mobileNavOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>
                    </div>
                    {mobileNavOpen && (
                        <div className="md:hidden bg-slate-950/95 backdrop-blur-xl border-t border-slate-800/30">
                            <div className="container mx-auto px-4 py-4 max-w-7xl">
                                <ul className="space-y-0.5">
                                    {NAV_ITEMS.map((item) => (
                                        <li key={item}>
                                            <button
                                                type="button"
                                                className="w-full text-left px-4 py-3 text-slate-300 hover:text-white hover:bg-white/[0.03] rounded-lg transition-colors text-sm font-medium"
                                                onClick={() => scrollToSection(item.toLowerCase())}
                                            >
                                                {item}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}
                </header>

                <main className="relative z-10">
                    <section className="w-full pt-24 sm:pt-32 pb-20 sm:pb-28 relative z-10">
                        <div className="container mx-auto px-4 sm:px-5 max-w-5xl">
                            <div className="flex flex-col items-center justify-center text-center">
                                <div className="flex flex-col items-center fade-in-up" style={ANIMATION_DELAYS.hero1}>
                                    <div className="inline-flex items-center mb-8 px-4 py-1.5 rounded-full bg-slate-800/50 border border-slate-700/40 hover:bg-slate-800/70 transition-colors cursor-default">
                                        <span className="w-1.5 h-1.5 rounded-full bg-teal-400 mr-2.5 animate-pulse"></span>
                                        <span className="category-label text-teal-400/80">Research Intelligence</span>
                                    </div>
                                    <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl max-w-4xl tracking-tight font-heading font-bold leading-[0.95] mb-8">
                                        <span className="text-white">Predictive Ecosystem</span>
                                        <br />
                                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-teal-300 via-indigo-300 to-violet-300">
                                            Intelligence & Mapping
                                        </span>
                                    </h2>

                                    <p className="text-base sm:text-lg text-slate-400 max-w-2xl mx-auto mb-12 fade-in-up font-light leading-relaxed" style={ANIMATION_DELAYS.hero2}>
                                        Signal detection, network topology, and strategic positioning across complex operating environments.
                                    </p>
                                </div>
                                <div className="fade-in-up w-full max-w-lg px-4" style={ANIMATION_DELAYS.hero3}>
                                    <p className="mt-5 text-[11px] uppercase tracking-[0.2em] text-slate-600 font-mono">
                                        Field Mapping · Signal Processing · Strategic Synthesis
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>

                    <div className="section-divider max-w-4xl mx-auto"></div>

                    <section id="services" className="container mx-auto px-4 sm:px-6 py-20 sm:py-28 max-w-7xl">
                        <ScrollReveal>
                            <div className="text-center max-w-3xl mx-auto mb-16">
                                <div className="flex items-center justify-center gap-4 mb-6">
                                    <div className="h-px w-12 bg-slate-700/40"></div>
                                    <p className="category-label text-teal-400/70">Services</p>
                                    <div className="h-px w-12 bg-slate-700/40"></div>
                                </div>
                                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-6 text-white tracking-tight">
                                    Research Services
                                </h2>
                                <p className="text-slate-400 text-lg font-light leading-relaxed">
                                    Structured analysis, signal detection, and strategic positioning for complex environments.
                                </p>
                            </div>
                        </ScrollReveal>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 relative z-10">
                            {SERVICES_DATA.map((service, index) => (
                                <ScrollReveal key={index} delay={index * 100}>
                                    <ServiceCard service={service} />
                                </ScrollReveal>
                            ))}
                        </div>
                    </section>

                    <div className="section-divider max-w-4xl mx-auto"></div>

                    <section id="methodology" className="py-20 sm:py-28 relative">
                        <div className="container mx-auto px-4 sm:px-6 max-w-7xl relative z-10">
                            <ScrollReveal>
                                <div className="text-center max-w-3xl mx-auto mb-16">
                                    <div className="flex items-center justify-center gap-4 mb-6">
                                        <div className="h-px w-12 bg-slate-700/40"></div>
                                        <p className="category-label text-indigo-400/70">Methodology</p>
                                        <div className="h-px w-12 bg-slate-700/40"></div>
                                    </div>
                                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-6 text-white tracking-tight">
                                        Intelligence Methodology
                                    </h2>
                                    <p className="text-lg text-slate-400 font-light leading-relaxed">
                                        A systematic approach to mapping complex environments and extracting actionable intelligence.
                                    </p>
                                </div>
                            </ScrollReveal>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {METHODOLOGY_STEPS.map((method, index) => (
                                    <ScrollReveal key={index} delay={index * 100}>
                                        <MethodologyCard method={method} stepNumber={index + 1} />
                                    </ScrollReveal>
                                ))}
                            </div>
                        </div>
                    </section>

                    <div className="section-divider max-w-4xl mx-auto"></div>

                    <section id="platform" className="py-20 sm:py-28 relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/50 via-slate-950 to-slate-950 pointer-events-none"></div>
                        <div className="container mx-auto px-4 sm:px-6 max-w-7xl relative z-10">
                            <ScrollReveal>
                                <div className="text-center max-w-3xl mx-auto mb-16">
                                    <div className="flex items-center justify-center gap-4 mb-6">
                                        <div className="h-px w-12 bg-slate-700/40"></div>
                                        <p className="category-label text-emerald-400/70">Platform</p>
                                        <div className="h-px w-12 bg-slate-700/40"></div>
                                    </div>
                                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-6 text-white tracking-tight">
                                        Intelligence Platform
                                    </h2>
                                    <p className="text-lg text-slate-400 font-light leading-relaxed">
                                        A live surface for reading structural movement, interpreting context, and navigating complex environments.
                                    </p>
                                </div>
                            </ScrollReveal>

                            <ScrollReveal>
                                <div className="relative mt-8 mb-20 sm:mb-16">
                                    <PlatformPreview />

                                    <div className="absolute -bottom-10 sm:-bottom-5 left-1/2 transform -translate-x-1/2 w-full max-w-lg z-10 px-4">
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                            {featurePills.map((feature, index) => (
                                                <div
                                                    key={index}
                                                    className="bg-slate-900/80 backdrop-blur-md rounded-full px-4 py-2 flex items-center justify-center border border-slate-700/40 shadow-lg hover:border-slate-600/50 transition-colors"
                                                >
                                                    <feature.icon className="w-3.5 h-3.5 text-slate-400 mr-2" />
                                                    <span className="text-xs font-semibold text-slate-300">{feature.label}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </ScrollReveal>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-6">
                                {PLATFORM_FEATURES.map((feature, index) => (
                                    <ScrollReveal key={index} delay={index * 80}>
                                        <PlatformFeatureCard feature={feature} />
                                    </ScrollReveal>
                                ))}
                            </div>
                        </div>
                    </section>

                    <div className="section-divider max-w-4xl mx-auto"></div>

                    <section id="insights" className="py-20 sm:py-28 relative">
                        <div className="container mx-auto px-4 sm:px-6 max-w-7xl relative z-10">
                            <div className="grid gap-12 lg:grid-cols-[minmax(0,1fr)_390px] lg:gap-10">
                                <div>
                                    <ScrollReveal>
                                        <div className="mb-10 max-w-3xl">
                                            <div className="flex items-center gap-4 mb-6">
                                                <div className="h-px w-8 bg-slate-700/40"></div>
                                                <p className="category-label text-amber-400/70">Strategic Insights</p>
                                            </div>
                                            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold mb-6 text-white tracking-tight">
                                                Intelligence Briefings
                                            </h2>
                                            <p className="text-lg text-slate-400 font-light leading-relaxed max-w-2xl">
                                                Executive-grade analysis on structural shifts, strategic sensing, partnership architecture, and the operating systems behind durable ecosystems.
                                            </p>
                                        </div>
                                    </ScrollReveal>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 xl:gap-8">
                                        {ARTICLES_DATA.map((article, index) => (
                                            <ScrollReveal key={index} delay={index * 150}>
                                                <ArticleCard article={article} onClick={handleArticleClick} />
                                            </ScrollReveal>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <ScrollReveal delay={200}>
                                        <div className="sticky top-28 rounded-2xl border border-slate-800/70 bg-slate-950/60 p-6 backdrop-blur-xl">
                                            <div className="flex items-center justify-between mb-8 pb-6 border-b border-slate-800/40">
                                                <h3 className="text-lg font-bold text-white flex items-center">
                                                    <FileText className="w-4 h-4 text-slate-400 mr-2" />
                                                    Recent Briefs
                                                </h3>
                                                <span className="category-label text-slate-600">Library</span>
                                            </div>
                                            <div className="space-y-2">
                                                {BLOG_POSTS_DATA.map((post, index) => (
                                                    <BlogItem key={index} post={post} onClick={handleArticleClick} />
                                                ))}
                                            </div>
                                        </div>
                                    </ScrollReveal>
                                </div>
                            </div>
                        </div>
                    </section>

                    <div className="section-divider max-w-4xl mx-auto"></div>

                    <section id="contact" className="container mx-auto px-4 sm:px-5 py-16 sm:py-24 max-w-6xl">
                        <ScrollReveal>
                            <div className="glass-card rounded-2xl p-8 sm:p-12 relative overflow-hidden border border-slate-800/40">
                                <div className="absolute top-0 right-0 -mr-20 -mt-20 w-64 h-64 bg-gradient-to-br from-teal-500/5 to-indigo-500/5 blur-3xl rounded-full pointer-events-none"></div>
                                <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-64 h-64 bg-gradient-to-br from-violet-500/5 to-rose-500/5 blur-3xl rounded-full pointer-events-none"></div>
                                
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 relative z-10">
                                    <div className="flex flex-col justify-center">
                                        <div className="inline-block mb-4 px-3 py-1 rounded-full bg-slate-800/50 border border-slate-700/40 w-fit">
                                            <span className="category-label text-teal-400/80">Contact</span>
                                        </div>
                                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-6 text-white tracking-tight">
                                            Request Analysis
                                        </h2>
                                        <p className="text-base text-slate-400 mb-8 leading-relaxed max-w-md">
                                            Schedule a consultation to discuss your operating environment and explore how structured intelligence can inform positioning.
                                        </p>
                                        <ul className="space-y-3 mb-8">
                                            {contactBenefits.map((item, index) => (
                                                <li key={index} className="flex items-center">
                                                    <div className="bg-slate-800 rounded-full p-1 mr-3 border border-slate-700/40">
                                                        <Target className="h-3 w-3 text-slate-500" />
                                                    </div>
                                                    <span className="text-slate-300 text-sm font-light">{item.text}</span>
                                                </li>
                                            ))}
                                        </ul>
                                        <a href="mailto:info@pteraresearch.com" className="flex items-center space-x-3 text-slate-400 hover:text-teal-400 transition-colors w-fit group">
                                            <Mail className="w-4 h-4" />
                                            <span className="text-sm font-medium">info@pteraresearch.com</span>
                                        </a>
                                    </div>

                                    <div className="bg-slate-900/40 rounded-xl border border-slate-800/40 p-6 sm:p-8">
                                        <h3 className="text-lg font-bold mb-6 text-white">Get in Touch</h3>
                                        {formSubmitted && (
                                            <div className="bg-emerald-500/5 border border-emerald-500/15 rounded-lg p-4 text-center mb-6">
                                                <p className="text-emerald-400 text-sm">Message sent.</p>
                                                <p className="text-emerald-500/60 text-xs mt-1">We will respond within 24 hours.</p>
                                            </div>
                                        )}
                                        {formError && (
                                            <div className="bg-rose-500/5 border border-rose-500/15 rounded-lg p-4 text-center mb-6 flex items-center justify-center gap-2">
                                                <AlertCircle className="w-4 h-4 text-rose-400" />
                                                <p className="text-rose-400 text-sm">{formError}</p>
                                            </div>
                                        )}
                                        <form className="space-y-4" onSubmit={handleFormSubmit}>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-[10px] font-medium text-slate-500 mb-1.5 uppercase tracking-wider">First Name</label>
                                                    <input name="firstName" type="text" required className="w-full bg-slate-900/50 border border-slate-800 rounded-lg px-4 py-2.5 text-slate-200 focus:outline-none focus:ring-1 focus:ring-slate-600 focus:border-slate-600 transition-all text-sm" placeholder="Name" />
                                                </div>
                                                <div>
                                                    <label className="block text-[10px] font-medium text-slate-500 mb-1.5 uppercase tracking-wider">Last Name</label>
                                                    <input name="lastName" type="text" required className="w-full bg-slate-900/50 border border-slate-800 rounded-lg px-4 py-2.5 text-slate-200 focus:outline-none focus:ring-1 focus:ring-slate-600 focus:border-slate-600 transition-all text-sm" placeholder="Surname" />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-medium text-slate-500 mb-1.5 uppercase tracking-wider">Email</label>
                                                <input name="email" type="email" required className="w-full bg-slate-900/50 border border-slate-800 rounded-lg px-4 py-2.5 text-slate-200 focus:outline-none focus:ring-1 focus:ring-slate-600 focus:border-slate-600 transition-all text-sm" placeholder="you@organization.com" />
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-medium text-slate-500 mb-1.5 uppercase tracking-wider">Organization</label>
                                                <input name="company" type="text" className="w-full bg-slate-900/50 border border-slate-800 rounded-lg px-4 py-2.5 text-slate-200 focus:outline-none focus:ring-1 focus:ring-slate-600 focus:border-slate-600 transition-all text-sm" placeholder="Company or affiliation" />
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-medium text-slate-500 mb-1.5 uppercase tracking-wider">Message</label>
                                                <textarea name="message" rows={3} required className="w-full bg-slate-900/50 border border-slate-800 rounded-lg px-4 py-2.5 text-slate-200 focus:outline-none focus:ring-1 focus:ring-slate-600 focus:border-slate-600 transition-all text-sm" placeholder="Describe your operating environment..."></textarea>
                                            </div>
                                            <Button className="w-full bg-slate-100 text-slate-900 hover:bg-white font-bold py-3 mt-2 text-xs tracking-wider disabled:opacity-40 disabled:cursor-not-allowed" type="submit" disabled={formSubmitting}>
                                                <span className="flex items-center justify-center">
                                                    {formSubmitting ? (
                                                        <>
                                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                            Processing...
                                                        </>
                                                    ) : (
                                                        <>
                                                            Send Request
                                                            <ArrowRight className="w-4 h-4 ml-2" />
                                                        </>
                                                    )}
                                                </span>
                                            </Button>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </ScrollReveal>
                    </section>

                    <footer className="relative mt-8">
                        <div className="h-px w-full bg-gradient-to-r from-transparent via-slate-700/50 to-transparent" />
                        <div className="container mx-auto px-4 sm:px-6 pt-14 pb-8 max-w-7xl">
                            <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">
                                <div className="md:col-span-5 flex flex-col gap-5">
                                    <button onClick={navigateHome} className="flex items-center group w-fit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-400/60 rounded-lg">
                                        <PTERALogo className="mr-3 group-hover:rotate-12 transition-transform duration-500" width={28} height={28} />
                                        <span className="text-base font-heading font-bold text-white tracking-tight">PTERA Research</span>
                                    </button>
                                    <p className="text-sm text-slate-400 leading-relaxed max-w-sm font-light">
                                        Predictive ecosystem intelligence and structured analysis for complex operating environments.
                                    </p>
                                    <a href="mailto:info@pteraresearch.com" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-teal-300 transition-colors w-fit group">
                                        <Mail className="w-4 h-4 text-slate-500 group-hover:text-teal-400 transition-colors" />
                                        <span>info@pteraresearch.com</span>
                                    </a>
                                </div>

                                <div className="md:col-span-3">
                                    <p className="text-[10px] font-semibold tracking-[0.28em] uppercase text-slate-500 mb-5">Navigate</p>
                                    <ul className="space-y-3">
                                        {NAV_ITEMS.map((item) => (
                                            <li key={item}>
                                                <button onClick={() => scrollToSection(item.toLowerCase())} className="text-sm text-slate-400 hover:text-white transition-colors">
                                                    {item}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div className="md:col-span-4">
                                    <p className="text-[10px] font-semibold tracking-[0.28em] uppercase text-slate-500 mb-5">Office</p>
                                    <address className="not-italic text-sm text-slate-400 leading-relaxed font-light">
                                        Unit 1113, Peninsula Centre<br />
                                        67 Mody Road, Tsim Sha Tsui<br />
                                        Kowloon, Hong Kong
                                    </address>
                                </div>
                            </div>

                            <div className="mt-12 pt-6 border-t border-slate-800/50 flex flex-col-reverse sm:flex-row items-center justify-between gap-4">
                                <p className="text-[11px] text-slate-500 tracking-wide">
                                    &copy; {new Date().getFullYear()} PTERA Research. All rights reserved.
                                </p>
                                <div className="flex items-center gap-6 text-[11px]">
                                    <button onClick={() => navigateToLegalPage('privacy')} className="text-slate-500 hover:text-slate-200 transition-colors tracking-wide">
                                        Privacy Policy
                                    </button>
                                    <button onClick={() => navigateToLegalPage('terms')} className="text-slate-500 hover:text-slate-200 transition-colors tracking-wide">
                                        Terms of Use
                                    </button>
                                </div>
                            </div>
                        </div>
                    </footer>
                </main>
            </div>
        </div>
    );
};

export default PTERAResearch;
